import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { MapPin, ArrowRight, Star, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import WhatsAppHero from "./WhatsAppHero";

const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

const API_BASE = `${API_URL}api/get-properties`;

const TopRatedProperties = () => {
  const [properties, setProperties] = useState([]);
  const [activeTab, setActiveTab] = useState("bungalow");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  function slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")      // replace spaces & special chars with hyphen
      .replace(/^-+|-+$/g, "");          // trim hyphens from start & end
  }


  // fetch properties from API
  const fetchProperties = async (propertyType) => {
    try {
      setLoading(true);
      let url = `${API_BASE}?propertyType=${propertyType}&minRate=0&maxRate=2000&page=1&limit=3`;
      if (propertyType == 'rental') {
        url = `${API_BASE}?rentalType=${propertyType}&minRate=0&maxRate=2000&page=1&limit=3`;
      }
      const res = await axios.get(url
      );
      setProperties(res.data.data || []); // <-- use .data.data
    } catch (error) {
      console.error("Error fetching properties:", error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };


  // load default (bungalows)
  useEffect(() => {
    fetchProperties(activeTab);
  }, [activeTab]);

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-fade-up">
          <h2 className="font-display-bold text-4xl md:text-5xl text-primary mb-6">
            Top Rated Properties in Solapur
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover our highest-rated properties across bungalows, rowhouses, and land categories.
          </p>
        </div>

        <Tabs
          defaultValue="bungalow"
          value={activeTab}
          onValueChange={(val) => setActiveTab(val)}
          className="w-full"
        >
          {/* Tab Buttons */}
          {/* Tab Buttons */}
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            <TabsTrigger
              value="bungalow"
              className="flex flex-col items-center justify-center rounded-2xl shadow-md px-4 py-3 text-sm sm:text-base md:text-lg font-medium transition-all duration-300 hover:scale-105 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-500 hover:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-purple-600 data-[state=active]:text-white"
            >
              🏠
              <span>Bungalows</span>
            </TabsTrigger>

            <TabsTrigger
              value="rowhouse"
              className="flex flex-col items-center justify-center rounded-2xl shadow-md px-4 py-3 text-sm sm:text-base md:text-lg font-medium transition-all duration-300 hover:scale-105 hover:bg-gradient-to-r hover:from-teal-500 hover:to-green-500 hover:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-600 data-[state=active]:to-green-600 data-[state=active]:text-white"
            >
              🏡
              <span>Rowhouses</span>
            </TabsTrigger>

            <TabsTrigger
              value="plot"
              className="flex flex-col items-center justify-center rounded-2xl shadow-md px-4 py-3 text-sm sm:text-base md:text-lg font-medium transition-all duration-300 hover:scale-105 hover:bg-gradient-to-r hover:from-yellow-500 hover:to-orange-500 hover:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-600 data-[state=active]:to-orange-600 data-[state=active]:text-white"
            >
              🏞️
              <span>Plots & Land</span>
            </TabsTrigger>

            <TabsTrigger
              value="flat"
              className="flex flex-col items-center justify-center rounded-2xl shadow-md px-4 py-3 text-sm sm:text-base md:text-lg font-medium transition-all duration-300 hover:scale-105 hover:bg-gradient-to-r hover:from-blue-500 hover:to-cyan-500 hover:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-600 data-[state=active]:text-white"
            >
              🏢
              <span>Flats</span>
            </TabsTrigger>

            <TabsTrigger
              value="shop"
              className="flex flex-col items-center justify-center rounded-2xl shadow-md px-4 py-3 text-sm sm:text-base md:text-lg font-medium transition-all duration-300 hover:scale-105 hover:bg-gradient-to-r hover:from-pink-500 hover:to-red-500 hover:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-600 data-[state=active]:to-red-600 data-[state=active]:text-white"
            >
              🛍️
              <span>Shops</span>
            </TabsTrigger>

            <TabsTrigger
              value="rental"
              className="flex flex-col items-center justify-center rounded-2xl shadow-md px-4 py-3 text-sm sm:text-base md:text-lg font-medium transition-all duration-300 hover:scale-105 hover:bg-gradient-to-r hover:from-gray-500 hover:to-gray-700 hover:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-gray-600 data-[state=active]:to-gray-800 data-[state=active]:text-white"
            >
              🔑
              <span>Rent</span>
            </TabsTrigger>
          </TabsList>




          {/* Tab Content */}
          <div
            style={{
              marginTop: window.innerWidth >= 1024
                ? "5rem"   // Desktop
                : window.innerWidth >= 768
                  ? "18rem"  // Tablet
                  : "20rem", // Mobile
            }}
          > <TabsContent value={activeTab} >
              {loading ? (
<div className="flex flex-col items-center justify-center py-20">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
      <span className="text-muted-foreground text-lg">Fetching properties...</span>
    </div>              ) : properties.length === 0 ? (
                <div className="text-center text-muted-foreground">
                  No properties found for {activeTab}.
                </div>
              ) : (
                <div className="grid lg:grid-cols-3 gap-8">
                  {properties && properties.map((property, index) => {

                    const id = property._id?.$oid || property._id;

                    return (
                      <Card
                        key={property._id}
                        className="luxury-card group cursor-pointer"
                        style={{ animationDelay: `${index * 0.2}s` }}
                      >
                        <div className="relative overflow-hidden">

                          <img
                            src={`https://dnrk6bi5jdn1c.cloudfront.net/${property.imageIds?.[0]}`}
                            alt={property.name || "Property"}
                            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute top-4 left-4 flex items-center gap-1 bg-white/90 px-2 py-1 rounded-full">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-medium">
                              {property.resale ? "Resale" : "New"}
                            </span>
                          </div>
                        </div>

                        <CardContent className="p-6 space-y-4">
                          <div className="space-y-2">
                            <h3 className="font-display-bold text-xl text-primary group-hover:text-luxury transition-colors">
                              {property.name || "Unnamed Property"}
                            </h3>
                            <div className="flex items-center text-muted-foreground">
                              <MapPin className="h-4 w-4 mr-2" />
                              <span>{property.location}</span>

                            </div>

                          </div>

                          {/* <p className="text-muted-foreground text-sm leading-relaxed">
                        {property.description || "Beautiful property in Solapur"}
                      </p> */}

                          <div className="grid grid-cols-2 gap-4 py-4 border-t border-border">
                            <div>
                              <div className="text-sm text-muted-foreground">Starting from</div>
                              <div className="font-bold text-primary">
                                {property.rate ? `₹${property.rate} ${property.priceUnit || ""}` : `₹${property.rent} ${property.rentUnit || ""}`}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-muted-foreground">Type</div>
                              <div className="font-medium">{property.propertyType.toUpperCase()}</div>
                            </div>
                            <div>
                              <div className="text-sm text-muted-foreground">Status</div>
                              <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full font-semibold shadow-md
      text-xs sm:text-sm
      ${!property.status || property.status === "Available"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                                }`}
                              >
                                {!property.status || property.status === "Available" ? (
                                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                                ) : (
                                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                                )}
                                <span>
                                  {(!property.status || property.status === "Available")
                                    ? "High Demand"
                                    : property.status
                                  }
                                </span>
                              </div>
                            </div>
                          </div>



                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-luxury hover:text-luxury-foreground hover:bg-luxury group"
                            onClick={() => {
                              navigate(`/properties/${slugify(property.name || "property")}-${id}`);
                            }}
                          >
                            View Details
                            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </CardContent>
                      </Card>
                    )

                  })}
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
      <WhatsAppHero />
    </section>
  );
};

export default TopRatedProperties;
